import { Dialog } from '#src/base/dialog/dialog.tsx';
import { cn } from '#src/styling/cn.ts';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Command as Primitive } from 'cmdk';
import type { ComponentProps } from 'react';

export type CommandRootProps = ComponentProps<typeof Primitive>;

const CommandRoot = ({ className, ...props }: CommandRootProps) => (
  <Primitive
    className={cn('flex h-full w-full flex-col overflow-hidden rounded-md bg-white', className)}
    {...props}
  />
);

export type CommandDialogProps = ComponentProps<typeof Dialog.Root> & {
  title: string;
  description: string;
  showCloseButton?: boolean;
  className?: string;
};

const CommandDialog = ({
  children,
  className,
  title,
  description,
  showCloseButton = true,
  ...props
}: CommandDialogProps) => (
  <Dialog.Root {...props}>
    <Dialog.Header className="sr-only">
      <Dialog.Title>{title}</Dialog.Title>
      <Dialog.Description>{description}</Dialog.Description>
    </Dialog.Header>
    <Dialog.Content
      className={cn('overflow-hidden p-0', className)}
      showCloseButton={showCloseButton}
    >
      <CommandRoot className="**:[[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group]]:px-2 **:[[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 **:[[cmdk-input-wrapper]_svg]:h-5 **:[[cmdk-input-wrapper]_svg]:w-5 **:[[cmdk-input]]:h-12 **:[[cmdk-item]_svg]:h-5 **:[[cmdk-item]_svg]:w-5 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3">
        {children}
      </CommandRoot>
    </Dialog.Content>
  </Dialog.Root>
);

export type CommandInputProps = ComponentProps<typeof Primitive.Input>;

const CommandInput = ({ className, ...props }: CommandInputProps) => (
  <div className="flex h-9 items-center gap-2 border-b px-3">
    <MagnifyingGlassIcon className="size-4 shrink-0 opacity-50" />
    <Primitive.Input
      className={cn(
        'placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  </div>
);

export type CommandListProps = ComponentProps<typeof Primitive.List>;

const CommandList = ({ className, ...props }: CommandListProps) => (
  <Primitive.List
    className={cn('max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto', className)}
    {...props}
  />
);

export type CommandEmptyProps = ComponentProps<typeof Primitive.Empty>;

const CommandEmpty = ({ ...props }: CommandEmptyProps) => (
  <Primitive.Empty className="py-6 text-center text-sm" {...props} />
);

export type CommandGroupProps = ComponentProps<typeof Primitive.Group>;

const CommandGroup = ({ className, ...props }: CommandGroupProps) => (
  <Primitive.Group
    className={cn(
      'text-foreground **:[[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium',
      className,
    )}
    {...props}
  />
);

export type CommandSeparatorProps = ComponentProps<typeof Primitive.Separator>;

const CommandSeparator = ({ className, ...props }: CommandSeparatorProps) => (
  <Primitive.Separator className={cn('bg-border -mx-1 h-px', className)} {...props} />
);

export type CommandItemProps = ComponentProps<typeof Primitive.Item>;

const CommandItem = ({ className, ...props }: CommandItemProps) => (
  <Primitive.Item
    className={cn(
      "data-[selected=true]:bg-primary/10 [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-pointer items-center gap-2 rounded-sm p-2 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      className,
    )}
    {...props}
  />
);

export type CommandShortcutProps = ComponentProps<'span'>;

const CommandShortcut = ({ className, ...props }: CommandShortcutProps) => (
  <span
    className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)}
    {...props}
  />
);

export const Command = {
  Root: CommandRoot,
  Dialog: CommandDialog,
  Input: CommandInput,
  List: CommandList,
  Empty: CommandEmpty,
  Group: CommandGroup,
  Separator: CommandSeparator,
  Item: CommandItem,
  Shortcut: CommandShortcut,
};
